// -----------------------------------------------------------------------------
// CompletionChecklist
//
// The "Wrap-up checklist" section of the TREATMENT-IN-PROGRESS hub. Presentational
// only: it receives the resolved checklist state (groups / upNext / initialLoading
// / templatesEmpty) as props from CompletedTreatmentHub, which owns the
// useCompletionChecklist hook so it can refresh() after complete/undo.
//
// Visual design mirrors the consultation popup's guided checklist (V2):
//   • stats strip   — Due today / Later / Done / Skipped / Paused chips + thin progress bar
//   • paused card   — 'Paused until <date>' + Resume (when nothing active is left
//     and items are paused), same look as AttendedGuidedChecklistV2's paused card
//   • featured card — UpNextCardV2 (the single up-next task, with its inline CTA)
//     + 50/50 inline ActionPanel split (passed in via the `actionPanel` slot)
//   • mini-grid     — ChecklistMiniGrid (the remaining items as mini-cards)
//   • Pause checklist — a single header control (the /pause/ endpoint pauses the
//     whole task-group), styled to match the consultation popup.
//
// The hub's ACTION SET is intentionally reduced — no outcome picker, no
// bulk-assign, no attendance gate — only complete/skip/undo/pause/change-due-date.
//
// Graceful degradation:
//   - slim skeleton while initialLoading
//   - renders nothing when templatesEmpty (no tasks-v2 / no templates) or empty
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { Box, Typography, Skeleton, Button, IconButton } from '@mui/material';
import Iconify from 'src/components/Iconify';
import ChecklistMiniGrid from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/ChecklistMiniGrid';
import RecentlyCompletedCard from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/RecentlyCompletedCard';
import UpNextCardV2, { TaskAssigneeHeads } from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/UpNextCardV2';
import RescheduleDialog from 'src/sections/@dashboard/leadDetail/AttendedGuidedChecklist/v2/RescheduleDialog';
import { parseApiDate } from 'src/utils/taskDateUtils';
import { CRM_PRIMARY_BUTTON_SX } from 'src/styles/crmButtons';
import { getItemOutcome, formatPausedUntil } from './checklistOutcome';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

export default function CompletionChecklist({
    initialLoading,
    templatesEmpty,
    groups,
    upNext,
    showProgress,
    progressDone,
    progressTotal,
    skippedCount,
    pausedCount,
    onResumeChecklist,
    resuming,
    leadEmail,
    practiceId,
    onItemClick,
    onUndo,
    onSkip,
    onChangeDueDate,
    onPauseChecklist,
    actionPanel,
    recentlyCompleted,
    onDismissRecentlyCompleted,
    onUndoRecentlyCompleted,
    onMarkComplete,
    showRescheduleBanner,
    setShowRescheduleBanner,
    bannerRescheduleOpen,
    setBannerRescheduleOpen,
    leadId,
    onLeadTasksReassigned,
}) {
    // Loading state — slim skeleton so the hub doesn't jump.
    if (initialLoading) {
        return (
            <Box sx={{ p: 3 }}>
                <Skeleton variant="rounded" height={28} sx={{ borderRadius: '8px', mb: 2 }} />
                <Skeleton variant="rounded" height={140} sx={{ borderRadius: '14px' }} />
            </Box>
        );
    }

    // Nothing applicable — render nothing (no tasks-v2, no templates, or empty).
    if (templatesEmpty) return null;

    const { today = [], later = [], done = [] } = groups || {};
    const hasAnything = today.length + later.length + done.length > 0;
    if (!hasAnything) return null;

    // Pause is checklist-wide (the /pause/ endpoint pauses the whole task-group),
    // so it's a single header control — only when there are active items to pause.
    const hasActive = today.length + later.length > 0;
    const hasOverdue = today.some((item) => item.due_at && new Date(parseApiDate(item.due_at)) < new Date());
    // Paused state: items paused and nothing active left to work (the paused
    // items sit in `done`, which is "out of the active queue"). Mirrors V2's
    // cascade-paused card; Resume reopens one paused item (server resumes the rest).
    const pausedItems = done.filter((item) => getItemOutcome(item) === 'paused');
    const firstPaused = pausedItems[0];
    const showPausedCard = !hasActive && !!firstPaused;
    const pausedUntilLabel = formatPausedUntil(firstPaused?.paused_until);
    const pausedOnTitle = firstPaused?.title || firstPaused?.taskTitle || null;
    // Genuinely completed steps only (skipped + paused excluded) for the copy.
    const completedCount = done.filter((item) => getItemOutcome(item) === 'done').length;
    const todayChipColor = hasOverdue ? '#FF5630' : '#FFAB00';
    const progressPct = progressTotal > 0 ? (progressDone / progressTotal) * 100 : 0;

    // onPause is per-item in UpNextCardV2 but the hub pauses the whole checklist —
    // forward to the single checklist-wide handler.
    const handleFeaturedPause = () => {
        if (onPauseChecklist) onPauseChecklist();
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Stats strip — Due today / Later / Done chips + progress bar. Mirrors
                the consultation popup's gchk-stats-strip. */}
            {showProgress && (
                <Box
                    data-testid="hub-stats-strip"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: 28,
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {today.length > 0 && (
                            <Box data-testid="hub-stats-chip-today" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: todayChipColor, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                    {today.length} Due today
                                </Typography>
                            </Box>
                        )}
                        {later.length > 0 && (
                            <Box data-testid="hub-stats-chip-later" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6e6e73', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                    {later.length} Later
                                </Typography>
                            </Box>
                        )}
                        {progressDone > 0 && (
                            <Box data-testid="hub-stats-chip-done" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#34c759', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                    {progressDone} Done
                                </Typography>
                            </Box>
                        )}
                        {skippedCount > 0 && (
                            <Box data-testid="hub-stats-chip-skipped" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#8e8e93', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                    {skippedCount} Skipped
                                </Typography>
                            </Box>
                        )}
                        {pausedCount > 0 && (
                            <Box data-testid="hub-stats-chip-paused" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#FFAB00', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#3c3c43', letterSpacing: '-0.005em' }}>
                                    {pausedCount} Paused
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Pause checklist — single checklist-wide control. */}
                    {onPauseChecklist && hasActive && (
                        <Button
                            data-testid="pause-checklist-btn"
                            size="small"
                            onClick={onPauseChecklist}
                            startIcon={<Iconify icon="eva:pause-circle-outline" width={16} height={16} />}
                            sx={{
                                flexShrink: 0,
                                color: '#6e6e73',
                                textTransform: 'none',
                                fontSize: '12px',
                                fontWeight: 600,
                                borderRadius: '8px',
                                px: 1.25,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#1d1d1f' },
                            }}
                        >
                            Pause checklist
                        </Button>
                    )}

                    {/* Progress bar — flex:1 so it fills the remaining strip width. */}
                    <Box sx={{ flex: 1, maxWidth: 320, ml: 1.5, height: 4, borderRadius: '2px', bgcolor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                        <Box
                            data-testid="hub-progress-bar-fill"
                            sx={{
                                height: '100%',
                                width: `${progressPct}%`,
                                bgcolor: '#34c759',
                                borderRadius: '2px',
                                transition: 'width 400ms ease',
                            }}
                        />
                    </Box>
                </Box>
            )}

            {/* Paused card — same visual language as AttendedGuidedChecklistV2's
                cascade-paused card. */}
            {showPausedCard && (
                <Box
                    data-testid="checklist-paused-card"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 4,
                        px: 3,
                        mb: 3,
                        bgcolor: 'rgba(255,171,0,0.06)',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,171,0,0.20)',
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            bgcolor: 'rgba(255,171,0,0.12)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5,
                        }}
                    >
                        <Iconify icon="mdi:pause-circle" width={28} height={28} sx={{ color: '#FFAB00' }} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.022em', mb: 0.5 }}>
                        {pausedUntilLabel ? `Paused until ${pausedUntilLabel}` : 'Checklist paused'}
                    </Typography>
                    {pausedOnTitle && (
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.01em', mb: 0.5 }}>
                            Next up: {pausedOnTitle}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: '13px', color: '#6e6e73', letterSpacing: '-0.01em', mb: 2.5, maxWidth: 280 }}>
                        {completedCount > 0
                            ? `${completedCount} step${completedCount !== 1 ? 's' : ''} already done — remaining steps will resume from here.`
                            : 'Remaining steps are snoozed and will resume from here.'}
                    </Typography>
                    {onResumeChecklist && (
                        <Button
                            data-testid="checklist-resume-btn"
                            disabled={resuming}
                            variant="contained"
                            disableElevation
                            onClick={() => onResumeChecklist(firstPaused)}
                            sx={{
                                ...CRM_PRIMARY_BUTTON_SX,
                                fontSize: '14px',
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                textTransform: 'none',
                                borderRadius: '6px',
                                px: 3,
                                py: 0.875,
                            }}
                        >
                            Resume checklist
                        </Button>
                    )}
                </Box>
            )}

            {/* Featured up-next card + inline 50/50 ActionPanel split.
                Mirrors AttendedGuidedChecklistV2.jsx:1200-1375.
                Ack overlay: UpNextCardV2 always rendered (size reference), hidden via
                visibility within its own position:relative parent during the 4s ack window;
                RecentlyCompletedCard positioned absolutely within that wrapper — zero layout shift.
                ActionPanel remains visible and interactive during ack. */}
            {upNext && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: 2,
                        // Breathing room between the featured/main task card and the
                        // mini-grid of remaining items below it.
                        mb: 3,
                        '& > [data-testid="action-panel"]': { flex: '0 0 70%', height: '100%', display: 'flex', flexDirection: 'column' },
                        '@media (max-width: 900px)': {
                            '& > [data-testid="action-panel"]': { flex: '0 0 100%' },
                        },
                    }}
                >
                    {/* Left column — UpNext card only.
                        Equal-height with ActionPanel: parent row is alignItems:stretch,
                        so this column fills the row height; UpNext card height:100% matches
                        it to a taller ActionPanel instead of leaving empty space beneath. */}
                    <Box
                        sx={{
                            flex: actionPanel ? '1 1 0' : '1',
                            minWidth: 0,
                            alignSelf: 'stretch',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: `flex 320ms ${EASING}`,
                            // No explicit height: it would disable align-self:stretch and collapse
                            // this column to content height. alignSelf:stretch fills the row; the
                            // card below uses height:100% to fill this (now-stretched) column.
                            ...(actionPanel ? { '& [data-testid="upnext-card-v2"]': { height: '100%' } } : {}),
                        }}
                    >
                        {/* Reschedule banner — shown in steady-state, hidden during ack.
                            Armed by onMarkComplete success. */}
                        {!recentlyCompleted && showRescheduleBanner && upNext && (
                            <Box
                                data-testid="reschedule-banner"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1.5,
                                    px: 2,
                                    py: 1.25,
                                    bgcolor: 'rgba(7,141,238,0.06)',
                                    border: '1px solid rgba(7,141,238,0.18)',
                                    borderRadius: '10px',
                                    flexWrap: 'wrap',
                                    rowGap: '8px',
                                }}
                            >
                                <Iconify icon="mdi:calendar-clock" width={16} height={16} sx={{ color: '#078DEE', flexShrink: 0 }} />
                                <Typography sx={{ flex: 1, fontSize: '12px', color: '#3c3c43', letterSpacing: '-0.005em', minWidth: 0 }}>
                                    Would you like to reschedule or assign your next task?
                                </Typography>
                                {upNext?.taskId && practiceId && (
                                    <Box data-testid="reschedule-banner-assignee" sx={{ flexShrink: 0 }}>
                                        <TaskAssigneeHeads
                                            practiceId={practiceId}
                                            taskId={upNext.taskId}
                                            initialAssignees={upNext.rawTask?.assignees || []}
                                            leadId={leadId}
                                            onLeadTasksReassigned={onLeadTasksReassigned}
                                        />
                                    </Box>
                                )}
                                <Button
                                    data-testid="reschedule-banner-btn"
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    onClick={() => setBannerRescheduleOpen?.(true)}
                                    sx={{
                                        fontSize: '11px',
                                        py: 0.4,
                                        px: 1.25,
                                        borderRadius: '6px',
                                        flexShrink: 0,
                                        bgcolor: '#078DEE',
                                        color: '#fff',
                                    }}
                                >
                                    Reschedule
                                </Button>
                                <IconButton
                                    data-testid="reschedule-banner-dismiss"
                                    size="small"
                                    aria-label="Dismiss"
                                    onClick={() => setShowRescheduleBanner?.(false)}
                                    sx={{ color: '#6e6e73', p: 0.25, flexShrink: 0, '&:hover': { color: '#1d1d1f' } }}
                                >
                                    <Iconify icon="eva:close-fill" width={16} height={16} />
                                </IconButton>
                                <RescheduleDialog
                                    open={bannerRescheduleOpen}
                                    onClose={() => setBannerRescheduleOpen?.(false)}
                                    onConfirm={(newDate) => {
                                        onChangeDueDate?.(upNext, newDate);
                                        setShowRescheduleBanner?.(false);
                                    }}
                                    item={upNext}
                                />
                            </Box>
                        )}
                        {/* Up Next card + ack overlay wrapper.
                            position:relative on this inner container so the absolute overlay
                            covers only the card, not the ActionPanel. */}
                        {upNext && (
                            <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ visibility: recentlyCompleted ? 'hidden' : 'visible', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                    <UpNextCardV2
                                        key={upNext?.id ?? 'none'}
                                        item={upNext}
                                        leadEmail={leadEmail}
                                        onActionClick={onItemClick}
                                        onSkip={onSkip}
                                        onPause={handleFeaturedPause}
                                        onChangeDueDate={onChangeDueDate}
                                        nextItem={null}
                                        nextEmptyReason={null}
                                        onItemClick={onItemClick}
                                        practiceId={practiceId}
                                        panelOpen={!!actionPanel}
                                        onMarkComplete={onMarkComplete}
                                        leadId={leadId}
                                        onLeadTasksReassigned={onLeadTasksReassigned}
                                    />
                                </Box>
                                {recentlyCompleted && (
                                    <Box data-testid="upnext-ack-window" sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
                                        <RecentlyCompletedCard
                                            item={recentlyCompleted.completed}
                                            label={recentlyCompleted.label}
                                            onDismiss={onDismissRecentlyCompleted || (() => {})}
                                            onUndo={onUndoRecentlyCompleted}
                                            fillHeight
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    {/* Right column — action panel. Remains visible during ack. */}
                    {actionPanel}
                </Box>
            )}

            {/* Mini-grid — the remaining checklist items as mini-cards. When there's
                no featured card, the ActionPanel (if open) renders above the grid. */}
            {/* Paused card replaces the panel (V2 returns the paused card alone). */}
            {!upNext && actionPanel && !showPausedCard && (
                <Box sx={{ mb: 3 }}>{actionPanel}</Box>
            )}
            <ChecklistMiniGrid
                groups={groups}
                featuredItem={upNext}
                onItemClick={onItemClick}
                onUndo={onUndo}
                onSkip={onSkip}
                onChangeDueDate={onChangeDueDate}
            />
        </Box>
    );
}

CompletionChecklist.propTypes = {
    initialLoading: PropTypes.bool,
    templatesEmpty: PropTypes.bool,
    groups: PropTypes.shape({
        today: PropTypes.array,
        later: PropTypes.array,
        done: PropTypes.array,
    }),
    /** The single up-next task (from the hook's selectUpNext) — rendered as the featured card. */
    upNext: PropTypes.object,
    /** Whether to show the stats strip + progress bar. */
    showProgress: PropTypes.bool,
    progressDone: PropTypes.number,
    progressTotal: PropTypes.number,
    /** Skipped / paused item counts for their own stats chips (shown only when > 0). */
    skippedCount: PropTypes.number,
    pausedCount: PropTypes.number,
    /** Resume from the paused card — called with a paused item (hub reopens it via /reopen/). */
    onResumeChecklist: PropTypes.func,
    /** True while the resume reopen is in flight — disables the Resume button. */
    resuming: PropTypes.bool,
    /** Lead email — forwarded to UpNextCardV2 (blocks email CTA when empty). */
    leadEmail: PropTypes.string,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onItemClick: PropTypes.func,
    onUndo: PropTypes.func,
    onSkip: PropTypes.func,
    onChangeDueDate: PropTypes.func,
    /** Checklist-wide pause (the /pause/ endpoint pauses the whole task-group). */
    onPauseChecklist: PropTypes.func,
    /** The hub's ActionPanel element, rendered in the inline 30% slot when an item is open. */
    actionPanel: PropTypes.node,
    /** task #22: ack state — set for 4s after a completion; hides UpNextCardV2 and shows RecentlyCompletedCard. */
    recentlyCompleted: PropTypes.object,
    /** Undo handler for the green ack card — reopens the just-completed item. */
    onUndoRecentlyCompleted: PropTypes.func,
    /** task #22: callback to dismiss the ack card manually (dismiss button). */
    onDismissRecentlyCompleted: PropTypes.func,
    /** task #22: forwarded to UpNextCardV2.onMarkComplete for direct card-level completion. */
    onMarkComplete: PropTypes.func,
    /** Reschedule banner visibility state. */
    showRescheduleBanner: PropTypes.bool,
    /** Setter for reschedule banner visibility. */
    setShowRescheduleBanner: PropTypes.func,
    /** Reschedule dialog open state. */
    bannerRescheduleOpen: PropTypes.bool,
    /** Setter for reschedule dialog open state. */
    setBannerRescheduleOpen: PropTypes.func,
    /** Lead this checklist belongs to — forwarded to TaskAssigneeHeads for the
        "Assign to all" → propagate-to-lead flow. */
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Forwarded to TaskAssigneeHeads — called after the lead's other open tasks
        are successfully reassigned. */
    onLeadTasksReassigned: PropTypes.func,
};

CompletionChecklist.defaultProps = {
    initialLoading: false,
    templatesEmpty: false,
    groups: { today: [], later: [], done: [] },
    upNext: null,
    showProgress: false,
    progressDone: 0,
    progressTotal: 0,
    skippedCount: 0,
    pausedCount: 0,
    onResumeChecklist: null,
    resuming: false,
    leadEmail: undefined,
    practiceId: undefined,
    onItemClick: null,
    onUndo: null,
    onSkip: null,
    onChangeDueDate: null,
    onPauseChecklist: null,
    actionPanel: null,
    recentlyCompleted: null,
    onDismissRecentlyCompleted: null,
    onUndoRecentlyCompleted: null,
    onMarkComplete: null,
    showRescheduleBanner: false,
    setShowRescheduleBanner: null,
    bannerRescheduleOpen: false,
    setBannerRescheduleOpen: null,
    leadId: null,
    onLeadTasksReassigned: null,
};
